import { useState, type FormEvent } from 'react'
import { PAGE_TITLES, useDocumentTitle } from '../lib/documentTitle'
import { motion } from 'framer-motion'
import { AlertTriangle, Send, Shield } from 'lucide-react'
import { Link } from 'react-router-dom'
import { API_BASE_URL, type SmbCloud } from '../lib/api'
import { SMB_CONTACT_EMAIL_KEY } from '../components/SmbGoogleAllowStep'

// DW-SMB-SIGNUP-LANDING.
//
// PUBLIC self-serve signup at /smb. The backend answers
// POST {API}/api/v1/smb-onboarding (app/routers/smb_onboarding.py) with a 201
// and sends a verification link that lands on /smb/verify — until this page
// existed, nothing in the SPA ever posted that URL, so a second company could
// not start the flow on its own.
//
// This page collects the SignupRequest fields (phone included — required, so
// the link always has a working SMS path), posts them once, and then STOPS at
// the generic success pane. The 201 body carries session_token / state /
// message — capability material for the DELIVERED link only. We never render
// any of it and never write dw_smb_session (that is /smb/verify's job after
// the round-trip proves control of the channel the link went out on); the
// only thing read out of the body is whether a channel took the link (see
// reportsNoChannel).
//
// Failures are deliberately generic: a 400/422 answers with the same
// check-details copy regardless of cause (no FastAPI `detail`, no account
// enumeration). No /verify or /consent call is ever made from here.
//
// DW-FE-CLIENT-HONESTY. The success pane only promises a link the API said it
// delivered. Signup fails closed: when neither the email nor the SMS channel
// accepts the magic link the backend deletes the session and answers 503, so
// a 503 is not "we are down for maintenance" — it is "that email and that
// mobile were not reachable". Telling that caller to open a link would be
// pointing them at a message that does not exist, so they get the
// undelivered pane instead: check the address and the number, and try again.
// The form stays on screen underneath so the retry needs no navigation.
//
// DW-SMB-SMS-IS-THE-DOOR. SMS is the channel that actually carries the link
// in production; the mail path is not wired up in the deployed environment
// (SendGrid is not minted). So the FORM says where the link is going —
// "Required. We text the link to this number." under Mobile — and says it
// once, on the field it is about. Nowhere on the form does the page tell
// anyone to watch an inbox. The success pane used to hedge as
// channel-neutral ("open the most recent link"), but a caller who was never
// sent an email has no email to check — telling them to open "a link" of
// unstated origin is the email-era lie carried into an SMS-only deployment.
// The pane now says plainly that we texted the number, with the same
// neutral send glyph (an envelope is still a promise of email the
// deployment cannot keep). Email is still collected and still posted — it
// is the account identity, and the backend may yet reach it — so the 503
// pane still names BOTH the address and the number as things to fix.
//
// BRIEF-B6b. The form asks first where the business email is, Microsoft 365
// or Google Workspace, and sends that as `cloud`. For a Google sign-up the
// email given here is kept in this browser (SMB_CONTACT_EMAIL_KEY) only so
// the Google allow step can prefill the administrator field; it is not a
// credential.
//
// BRIEF-E5e. The page says what it is: the same mark and name as the sign-in
// page, a heading, and one plain line of positioning (never "households",
// never "AI security analysts"). Neither cloud is chosen until the person
// picks one — the backend would read a cloud-less sign-up as Microsoft 365,
// so a Google business that never looked at the question would be put on the
// wrong cloud. Send is refused with a plain message until a cloud is chosen,
// and nothing leaves the page.
//
// BRIEF-E7. The browser's own IANA zone goes with the sign-up as `time_zone`,
// so the business's pages and night-time wakes are on its own clock from day
// one. A browser that cannot say sends none; the backend treats a missing or
// unknown zone as the platform's, never as an error.

/** The browser's IANA zone, or null when it cannot say. */
export function browserTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return typeof zone === 'string' && zone ? zone : null
  } catch {
    return null
  }
}

const POSITIONING_LINE =
  'DigitalWatch watches your Microsoft 365 or Google Workspace accounts every day and tells you when something needs you. For businesses of 1 to 15 people and family offices.'
const CHOOSE_CLOUD_MESSAGE = 'Choose where your business email is first.'

const CLOUD_CHOICES: { value: SmbCloud; label: string }[] = [
  { value: 'microsoft365', label: 'Microsoft 365' },
  { value: 'google_workspace', label: 'Google Workspace' },
]

type SignupState =
  | 'form'
  | 'submitting'
  | 'success'
  | 'undelivered'
  | 'error'

// True only when the body EXPLICITLY reports that nothing went out. Today's
// 201 never names a channel (SignupResponse is state + message), and a
// channel-silent 201 still means delivered — the backend would have 503'd
// otherwise. If a later contract does name the channel it used, an empty or
// null one is the same dead end as the 503, and the pane must not promise a
// link. Nothing else is read from the body: session_token stays off screen
// and out of storage, which is still /smb/verify's job after the round-trip.
function reportsNoChannel(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false
  const record = body as Record<string, unknown>
  if ('channels' in record) {
    return !Array.isArray(record.channels) || record.channels.length === 0
  }
  if ('channel' in record) {
    return !record.channel
  }
  return false
}

export function SmbSignupPage() {
  useDocumentTitle(PAGE_TITLES.smbSignup)
  const [state, setState] = useState<SignupState>('form')
  const [orgName, setOrgName] = useState('')
  const [primaryDomain, setPrimaryDomain] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [cloud, setCloud] = useState<SmbCloud | null>(null)
  const [cloudMissing, setCloudMissing] = useState(false)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    // The first question has to be answered: with no cloud chosen the send
    // is refused here, with a plain message on the question, and no fetch.
    if (cloud === null) {
      setCloudMissing(true)
      return
    }
    // Phone is required: the link goes out over it. Browser `required`
    // blocks a truly empty field; this catches whitespace-only, staying on
    // the form without a fetch. Non-empty is sent trimmed but otherwise
    // verbatim — no E.164 rewriting here.
    const trimmedPhone = contactPhone.trim()
    if (!trimmedPhone) {
      return
    }
    setState('submitting')
    try {
      const body: Record<string, string> = {
        org_name: orgName,
        primary_domain: primaryDomain,
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone: trimmedPhone,
        cloud,
      }
      const timeZone = browserTimeZone()
      if (timeZone) body.time_zone = timeZone
      // Unauthenticated by design — plain fetch, no Authorization header
      // (the shared axios client would attach any stored staff token).
      const res = await fetch(`${API_BASE_URL}/api/v1/smb-onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res.ok) {
        // The body is read for ONE thing — did a channel take the link — and
        // nothing else: session_token/state/message stay off the screen and
        // out of storage. A body that will not parse is not evidence of a
        // failure, so it keeps the delivered reading.
        let delivery: unknown = null
        try {
          delivery = await res.json()
        } catch {
          delivery = null
        }
        const delivered = !reportsNoChannel(delivery)
        if (delivered && cloud === 'google_workspace') {
          try {
            localStorage.setItem(SMB_CONTACT_EMAIL_KEY, contactEmail.trim())
          } catch {
            // Storage unavailable: the allow step simply starts blank.
          }
        }
        setState(delivered ? 'success' : 'undelivered')
      } else if (res.status === 503) {
        // Fail-closed signup: no channel accepted the link, no session.
        setState('undelivered')
      } else {
        setState('error')
      }
    } catch {
      setState('error')
    }
  }

  const inputClass =
    'w-full mt-1 px-3 py-2.5 rounded-lg border border-gray-300 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-brass focus:border-brass'
  const labelClass = 'block text-sm font-medium text-navy text-left'

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        {/* Same mark and name as the sign-in page */}
        <div className="flex flex-col items-center mb-8" data-brand-mark>
          <div className="w-14 h-14 rounded-2xl bg-brass/10 flex items-center justify-center mb-3">
            <Shield className="w-8 h-8 text-brass" aria-hidden="true" />
          </div>
          <p
            className="text-2xl font-serif tracking-wide"
            style={{ color: '#c49a5a' }}
          >
            DigitalWatch
          </p>
        </div>

        <div className="bg-cream rounded-2xl p-6 sm:p-8 shadow-xl">
          {state === 'success' ? (
            <div className="text-center" role="status" aria-live="polite">
              <div className="w-14 h-14 rounded-2xl bg-brass/10 flex items-center justify-center mx-auto mb-4">
                <Send className="w-7 h-7 text-brass" aria-hidden="true" />
              </div>
              <h1 className="text-xl font-serif font-bold text-navy">
                We texted this number
              </h1>
              <p className="text-sm text-gray-500 mt-2">
                Open that message to continue. Nothing has been set up yet.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center mt-6 tap-min px-5 py-2.5 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
              >
                Sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-serif font-bold text-navy text-center">
                Start your security operations centre
              </h1>
              <p
                className="text-sm text-navy/80 mt-3 text-center"
                data-what-this-is
              >
                {POSITIONING_LINE}
              </p>
              <p className="text-sm text-gray-500 mt-3 text-center">
                Tell us who to contact. We will send a link.
              </p>

              {state === 'undelivered' && (
                <div
                  className="mt-4 rounded-lg bg-danger-light p-4 text-center"
                  role="alert"
                >
                  <AlertTriangle
                    className="w-5 h-5 text-danger mx-auto mb-2"
                    aria-hidden="true"
                  />
                  <h2 className="text-sm font-serif font-bold text-navy">
                    We could not reach that email or mobile
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Check them and try again. No link was sent, and nothing has
                    been set up yet.
                  </p>
                </div>
              )}
              {state === 'error' && (
                <div
                  className="mt-4 rounded-lg bg-danger-light p-4 text-center"
                  role="alert"
                >
                  <AlertTriangle
                    className="w-5 h-5 text-danger mx-auto mb-2"
                    aria-hidden="true"
                  />
                  <h2 className="text-sm font-serif font-bold text-navy">
                    Please check the details and try again.
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Nothing has been set up yet.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <fieldset
                  data-cloud-choice
                  aria-describedby={cloudMissing ? 'smb-cloud-missing' : undefined}
                >
                  <legend className="block text-sm font-medium text-navy text-left mb-2">
                    Where is your business email?
                  </legend>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {CLOUD_CHOICES.map((choice) => {
                      const selected = cloud === choice.value
                      return (
                        <label
                          key={choice.value}
                          className={`relative flex items-center justify-center min-h-[56px] px-4 py-3 rounded-xl border-2 cursor-pointer text-base font-medium text-navy transition-colors focus-within:ring-2 focus-within:ring-brass focus-within:ring-offset-2 ${
                            selected
                              ? 'border-brass bg-brass/10'
                              : 'border-gray-300 bg-white hover:border-brass/60'
                          }`}
                        >
                          <input
                            type="radio"
                            name="smb-cloud"
                            value={choice.value}
                            checked={selected}
                            onChange={() => {
                              setCloud(choice.value)
                              setCloudMissing(false)
                            }}
                            className="sr-only"
                          />
                          {choice.label}
                        </label>
                      )
                    })}
                  </div>
                  {cloudMissing && (
                    <p
                      id="smb-cloud-missing"
                      role="alert"
                      className="text-sm text-danger mt-2 text-left"
                    >
                      {CHOOSE_CLOUD_MESSAGE}
                    </p>
                  )}
                </fieldset>
                <div>
                  <label htmlFor="smb-org-name" className={labelClass}>
                    Organisation
                  </label>
                  <input
                    id="smb-org-name"
                    type="text"
                    required
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="smb-primary-domain" className={labelClass}>
                    Domain
                  </label>
                  <input
                    id="smb-primary-domain"
                    type="text"
                    required
                    value={primaryDomain}
                    onChange={(e) => setPrimaryDomain(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="smb-contact-name" className={labelClass}>
                    Your name
                  </label>
                  <input
                    id="smb-contact-name"
                    type="text"
                    required
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="smb-contact-email" className={labelClass}>
                    Email
                  </label>
                  <input
                    id="smb-contact-email"
                    type="email"
                    required
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="smb-contact-phone" className={labelClass}>
                    Mobile
                  </label>
                  <input
                    id="smb-contact-phone"
                    type="tel"
                    required
                    autoComplete="tel"
                    placeholder="+64…"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    className={inputClass}
                  />
                  <p className="text-xs text-gray-500 mt-1 text-left">
                    Required. We text the link to this number.
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={state === 'submitting'}
                  className="w-full tap-min px-5 py-2.5 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2 disabled:opacity-60"
                >
                  Send the link
                </button>
              </form>

              <p className="text-sm text-gray-500 mt-6 text-center">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-brass font-medium hover:text-brass-dark"
                >
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  )
}
